import { Box, Button, Grid, Tab, Table, Tabs, Typography } from "components";
import { compose, withHooks, withTranslation } from "enhancers";
import { isEmpty, keyBy } from "lodash";
import styled from "styled-components";
import { AppColor } from "theme/app-color";
import { gql, toCurrency } from "utils/helper";
import ViewInsuranceButton from "../insurancePackages/ViewInsuranceButtonComponent";

const TableContainer = styled(Box)`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
`;

const CustomButton = styled(Button)`
  min-width: 110px;
  height: 34px;
  margin: 0 16px 8px 0;
  padding: 0 16px 0 16px;
  text-align: center;
`;

export interface InsuranceTypeInterface {
  id: string;
  name: string;
  symbol: string;
}

export const InsuranceTableComponent = (props: any) => (
  <Box display="flex" flexDirection="column">
    <Tabs value={props.tabState} onChange={props.changeTabState}>
      <Tab
        label={props.t(".tab1")}
        style={{
          fontWeight: 600,
          color:
            props.tabState === 0
              ? AppColor["Primary/Primary"]
              : AppColor["Text/Secondary"],
        }}
      />
      <Tab
        label={props.t(".tab2")}
        style={{
          fontWeight: 600,
          color:
            props.tabState === 1
              ? AppColor["Primary/Primary"]
              : AppColor["Text/Secondary"],
        }}
      />
    </Tabs>
    <Box>
      {props.tabState === 0 && (
        <Box>
          {!isEmpty(props.insurancesTableData) ? (
            <TableContainer>
              <Box mb={4}>
                {props.insuranceType.map((type: InsuranceTypeInterface) => (
                  <CustomButton
                    variant={
                      type.id === props.insuranceFilter.id
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() =>
                      props.setInsuranceFilter({
                        id: type.id,
                        name: type.name,
                      })
                    }
                  >
                    <Typography variant="Body/16">{type.name}</Typography>
                  </CustomButton>
                ))}
              </Box>
              <Typography my="11px" variant="body2">
                {props.t(".insuranceTableHeader", {
                  name: props.insuranceFilter.name,
                })}
              </Typography>
              <Table
                columns={props.columns}
                rows={props.insurancesTableData || []}
                density="compact"
                autoHeight
                style={{ minHeight: "40px" }}
              />
            </TableContainer>
          ) : (
            <TableContainer>
              <Typography
                my={2}
                variant="body1"
                textAlign="center"
                color="Text/Dark Grey"
              >
                {props.t(".notFoundData")}
              </Typography>
            </TableContainer>
          )}
        </Box>
      )}
      {props.tabState === 1 && (
        <Box>
          {!isEmpty(props.insurancePackagesTableData) ? (
            <TableContainer>
              <Box mb={4}>
                {props.insurancePackages.map((insurance: any) => (
                  <>
                    {/* {insurance.isFamily === props.isFamily && ( */}
                    <CustomButton
                      variant={
                        insurance.id === props.insurancePackageFilter.id
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() =>
                        props.setInsurancePackageFilter({
                          id: insurance.id,
                          name: insurance.name,
                        })
                      }
                    >
                      <Typography variant="Body/16">
                        {insurance.name}
                      </Typography>
                    </CustomButton>
                    {/* )} */}
                  </>
                ))}
              </Box>
              {/* <Grid container spacing={2} mb="27px">
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    {props.t(".pricingType")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    {props.insurancePackage.premium
                      ? toCurrency(props.insurancePackage.premium, {
                          maximumFractionDigits: 0,
                          minimumFractionDigits: 0,
                        })
                      : props.t(
                          `.pricingTypeText.${props.insurancePackage.packagePricingType}`
                        )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">{props.t(".level")}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    {props.insurancePackage.coverageLevel}
                  </Typography>
                </Grid>
              </Grid> */}
              <Typography my="11px" variant="body2">
                {props.insurancePackageFilter.name}
              </Typography>
              <Table
                columns={props.columnsPackage}
                rows={props.insurancePackagesTableData || []}
                density="compact"
                autoHeight
                style={{ minHeight: "40px" }}
              />
            </TableContainer>
          ) : (
            <TableContainer>
              <Typography
                my={2}
                variant="body1"
                textAlign="center"
                color="Text/Dark Grey"
              >
                {props.t(".notFoundPackage")}
              </Typography>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  </Box>
);

export const API = {
  FETCH_INSURANCE_DETAIL: gql`
    query FETCH_INSURANCE_DETAIL($id: String!) {
      masterInsuranceDetail(id: $id) {
        id
        year
        state
        subState
        companyName
        protectionStartDate
        protectionEndDate
        insurances {
          id
          nameTh
          nameEn
          premium
          remarkTh
          remarkEn
          description
          insuranceTypeId
          isFamily
          coverageLevel
        }
        insurancePackages {
          id
          nameTh
          nameEn
          remarkTh
          remarkEn
          packagePricingType
          insurancesInsurancePackages {
            id
            insurancePlanId
            packageId
          }
          isFamily
          coverageLevel
          description
          premium
        }
      }
    }
  `,
  FETCH_INSURANCE_TYPES: gql`
    query FETCH_INSURANCE_TYPES {
      insuranceTypes {
        id
        symbol
        nameTh
        nameEn
      }
    }
  `,
};

const enhancer = compose(
  withTranslation({ prefix: "pages.main.insurance.InsurancePreview" }),
  withHooks((props: any, hooks: any) => {
    const {
      useState,
      useLocation,
      useParams,
      useMemo,
      useQuery,
      useCallback,
      useDataTranslation,
    } = hooks;
    const { t, isFamily, masterInsurances } = props;

    const [tabState, setTabState] = useState(0);
    const [insuranceFilter, setInsuranceFilter] = useState({
      id: "",
      name: "",
    });
    const [insurancePackageFilter, setInsurancePackageFilter] = useState({
      id: "",
      name: "",
    });
    const [insurancePackage, setInsurancePackage] = useState({});
    const location = useLocation();
    const { hash: hashTag } = location || {};
    const { id } = useParams();

    const { data: types } = useQuery(API.FETCH_INSURANCE_TYPES);

    const insuranceTypesTranslated = useDataTranslation(types);

    const insurances = useMemo(() => {
      if (masterInsurances) {
        // const insurancePlans: any = {};
        // masterInsurances?.insurances.forEach((insurance: any) => {
        //   insurancePlans[insurance.id] = insurance;
        // });
        const insurancePlans = keyBy(masterInsurances?.insurances, "id");
        return insurancePlans;
      }
      return undefined;
    }, [masterInsurances]);

    const insuranceType = useMemo(() => {
      console.log(
        ">>> debug insurance types",
        insuranceTypesTranslated?.insuranceTypes
      );

      if (isEmpty(insuranceTypesTranslated?.insuranceTypes)) {
        return [];
      }

      const masterInsurancesTypeid = masterInsurances?.insurances.map(
        (insurance: any) => insurance.insuranceTypeId
      );

      console.log(">>> debug masterInsurancesTypeid", masterInsurancesTypeid);

      console.log(
        ">>> existed types",
        insuranceTypesTranslated.insuranceTypes.filter((insuranceType: any) =>
          masterInsurancesTypeid?.includes(insuranceType.id)
        )
      );

      const existedInsuranceTypes = insuranceTypesTranslated.insuranceTypes.filter(
        (insuranceType: any) =>
          masterInsurancesTypeid?.includes(insuranceType.id)
      );

      setInsuranceFilter({
        id: existedInsuranceTypes[0]?.id,
        name: existedInsuranceTypes[0]?.name,
      });

      return existedInsuranceTypes;
    }, [insuranceTypesTranslated, masterInsurances]);

    const insurancePackages = useMemo(() => {
      const packages = masterInsurances?.insurancePackages;

      if (isEmpty(packages) || isEmpty(insurances)) {
        return [];
      }

      const result = packages.map((packageItem: any) => {
        const insuranceDetail = packageItem.insurancesInsurancePackages.map(
          (relation: any) => insurances[relation.insurancePlanId]
        );
        return {
          ...packageItem,
          insurances: insuranceDetail,
        };
      });
      const packagesFiltered = packages;
      setInsurancePackageFilter({
        id: packagesFiltered[0]?.id,
        name: packagesFiltered[0]?.name,
      });
      return result;
    }, [masterInsurances, insurances]);

    // useEffect(() => {
    //   switch (hashTag) {
    //     case "#insuranceType":
    //       return setTabState(0);
    //     case "#insurancePackage":
    //       return setTabState(1);
    //     default:
    //       return setTabState(0);
    //   }
    // }, [hashTag]);

    const changeTabState = useCallback((e: any, value: any) => {
      setTabState(value);
      // switch (value) {
      //   case 0:
      //     return insuranceStepPath
      //       .insuranceValidation(id)
      //       .push(`#insuranceType`);
      //   // .push(`#insuranceType#${isFamily}`);
      //   case 1:
      //     return insuranceStepPath
      //       .insuranceValidation(id)
      //       .push(`#insurancePackage`);
      //   // .push(`#insurancePackage#${isFamily}`);
      //   default:
      //     return insuranceStepPath
      //       .insuranceValidation(id)
      //       .push(`#insuranceType`);
      //   // .push(`#insuranceType#${isFamily}`);
      // }
    }, []);

    const columns = useMemo(
      () => [
        {
          width: 200,
          field: "name",
          headerName: t(".insuranceName"),
        },
        {
          width: 100,
          field: "premium",
          headerName: t(".pricingType") || "",
          type: "textEnd",
        },
        // {
        //   width: 60,
        //   field: "coverageLevel",
        //   headerName: t(".level") || "",
        //   type: "textEnd",
        // },
        {
          width: 230,
          field: "remark",
          headerName: t(".insuranceRemark"),
        },
        {
          width: 55,
          field: "actions",
          headerName: " ",
          filterable: false,
          sortable: false,
          renderCell: (row: any) => (
            <ViewInsuranceButton insurancePlanId={row.id} />
          ),
        },
      ],
      [t]
    );
    const columnsPackage = useMemo(
      () => [
        {
          width: 290,
          field: "name",
          headerName: t(".insuranceName"),
        },
        {
          width: 290,
          field: "remark",
          headerName: t(".insuranceRemark"),
        },
        {
          width: 55,
          field: "actions",
          headerName: " ",
          filterable: false,
          sortable: false,
          renderCell: (row: any) => (
            <ViewInsuranceButton
              insurancePlanId={row.id}
              packagePricingType={row.row.packagePricingType}
            />
          ),
        },
      ],
      [t]
    );

    const insurancesTableData = useMemo(() => {
      if (isEmpty(insurances)) {
        return [];
      }

      const list = Object.keys(insurances).map((key) => insurances[key]);

      return list.filter(
        (insurances) => insurances.insuranceTypeId === insuranceFilter.id
      );
    }, [insurances, insuranceFilter]);

    const insurancePackagesTableData = useMemo(() => {
      if (isEmpty(insurancePackages)) {
        return [];
      }
      const insurancePackage = insurancePackages.find(
        (packageItem: any) => packageItem.id === insurancePackageFilter.id
      );
      setInsurancePackage(insurancePackage);
      const result =
        insurancePackage?.insurances.map((insurance: any) => {
          return {
            ...insurance,
            packagePricingType: insurancePackage.packagePricingType,
          };
        }) || [];

      const tableData: any[] = [];

      if (result) {
        result.map((data: any) => {
          tableData.push(data);
        });
      }

      return tableData;
    }, [insurancePackages, insurancePackageFilter]);

    return {
      tabState,
      changeTabState,
      columns,
      insuranceType,
      setInsuranceFilter,
      setInsurancePackageFilter,
      insurancesTableData,
      insuranceFilter,
      insurancePackages,
      insurancePackageFilter,
      insurancePackagesTableData,
      columnsPackage,
      t,
      isFamily,
      insurancePackage,
    };
  })
);
export default enhancer(InsuranceTableComponent);
